/* eslint-disable react/sort-comp */
import { Component } from 'react';
import PropTypes from 'prop-types';
import { flow } from 'lodash/fp';
import actions from '@store/actions';
import { connect } from 'react-redux';
import { Form } from '@ant-design/compatible';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import { Row, Input, Select } from 'antd';
import Button from '@components/shared/V2Button';
import Container from '@components/shared/Admin/Container';
import { withTranslation } from 'react-i18next';
import { parse } from 'qs';
import { debounce, sortBy } from 'lodash';
import api from '@shared/api';
import routes from '@root/routes';
import { withRouter, Link } from 'react-router-dom';
import queryStringSerializer from '@shared/queryStringSerializer';
import { historyType, locationType } from '@propTypes/index';
import PaginationRow from '@components/shared/PaginationRow';
import CustomIcon from '@components/shared/CustomIcon';
import BuildingsTable from '@components/shared/BuildingsTable';
import utils from '@styles/utils.module.less';
import s from './BuildingSearch.module.less';

const pageFrom = location => {
  const queryParams = parse(location.search, { ignoreQueryPrefix: true });
  return queryParams.page ? Number(queryParams.page) : 1;
};

const queryFrom = location => {
  const queryParams = parse(location.search, { ignoreQueryPrefix: true });
  return queryParams.query || '';
};

const spaceProviderSelectionsFrom = location => {
  const queryParams = parse(location.search, { ignoreQueryPrefix: true });
  return queryParams.space_providers || [];
};

const statusSelectionsFrom = location => {
  const queryParams = parse(location.search, { ignoreQueryPrefix: true });
  return queryParams.statuses || [];
};

const citySelectionsFrom = location => {
  const queryParams = parse(location.search, { ignoreQueryPrefix: true });
  return queryParams.cities || [];
};

const stateSelectionsFrom = location => {
  const queryParams = parse(location.search, { ignoreQueryPrefix: true });
  return queryParams.states || [];
};

const countrySelectionsFrom = location => {
  const queryParams = parse(location.search, { ignoreQueryPrefix: true });
  return queryParams.countries || [];
};

export class RawBuildingSearch extends Component {
  state = {
    buildings: [],
    pagination: {},
    queryValue: queryFrom(this.props.location),
    loading: true,
    spaceProviders: [],
    cities: [],
    states: [],
    countries: [],
    spaceProviderSelections: spaceProviderSelectionsFrom(this.props.location),
    statusSelections: statusSelectionsFrom(this.props.location),
    citySelections: citySelectionsFrom(this.props.location),
    stateSelections: stateSelectionsFrom(this.props.location),
    countrySelections: countrySelectionsFrom(this.props.location),
  };

  componentDidMount() {
    const { setBreadcrumbs, t } = this.props;
    this.fetchFilters();
    this.fetchBuildings();
    setBreadcrumbs([t('breadcrumbs.admin'), t('breadcrumbs.buildings')]);
  }

  componentDidUpdate(prevProps) {
    const { location } = this.props;
    const pageChanged = pageFrom(location) !== pageFrom(prevProps.location);
    const queryChanged = queryFrom(location) !== queryFrom(prevProps.location);
    if (pageChanged || queryChanged) {
      this.fetchBuildings();
    }
  }

  componentWillUnmount() {
    const { removeBreadcrumbs } = this.props;
    removeBreadcrumbs();
  }

  onSearch = debounce(() => {
    this.pushToHistory();
  }, 250);

  buildingTableActionButtons = id => {
    const { t } = this.props;
    return (
      <>
        <Link to={routes.admin.building(id)}>{t('building.editBuildingButton')}</Link>
        <Link style={{ marginLeft: 20 }} to={routes.admin.buildingListings(id)}>
          {t('building.manageListingsButton')}
        </Link>
      </>
    );
  };

  fetchBuildings = async () => {
    this.setState({ loading: true });
    const { location } = this.props;
    const {
      spaceProviderSelections,
      statusSelections,
      citySelections,
      stateSelections,
      countrySelections,
    } = this.state;
    const response = await api.fetch(
      routes.api.admin.buildingSearch({
        query: queryFrom(location),
        page: pageFrom(location),
        spaceProviders: spaceProviderSelections,
        statuses: statusSelections,
        cities: citySelections,
        states: stateSelections,
        countries: countrySelections,
      }),
    );
    const { buildings, pagination } = await response.json();

    this.setState({ buildings, pagination, loading: false });
  };

  fetchFilters = async () => {
    const response = await api.fetch(routes.api.admin.buildingFilters);
    const { companies, localities, regions, countryCodes } = await response.json();

    this.setState({
      spaceProviders: companies,
      cities: localities,
      states: regions,
      countries: countryCodes,
    });
  };

  renderSpaceProviderFilterOptions = () =>
    this.state.spaceProviders.map(spaceProvider => (
      <Select.Option key={spaceProvider.id} value={spaceProvider.id}>
        {spaceProvider.name}
      </Select.Option>
    ));

  renderFilterOptions = options =>
    options.map(option => (
      <Select.Option key={option} value={option}>
        {option}
      </Select.Option>
    ));

  renderFilterOptionsForCountries = (countryCodeList, t) => {
    const countryList = [];
    countryCodeList.forEach(countryCode => {
      countryList.push({ countryCode, countryName: t(`country:${countryCode}`) });
    });
    const countryListSorted = sortBy(countryList, ['countryName']);

    return countryListSorted.map(({ countryCode, countryName }) => (
      <Select.Option key={countryCode} value={countryCode} label={countryName}>
        {countryName}
      </Select.Option>
    ));
  };

  handleSetSpaceProviderFilterSelections = spaceProviderSelections => {
    this.setState({ spaceProviderSelections }, () => {
      this.pushToHistory();
      this.fetchBuildings();
    });
  };

  handleSetStatusFilterSelections = statusSelections => {
    this.setState({ statusSelections }, () => {
      this.pushToHistory();
      this.fetchBuildings();
    });
  };

  handleSetCityFilterSelections = citySelections => {
    this.setState({ citySelections }, () => {
      this.pushToHistory();
      this.fetchBuildings();
    });
  };

  // TODO rename to handleSetRegionFilterSelections ?
  handleSetStateFilterSelections = stateSelections => {
    this.setState({ stateSelections }, () => {
      this.pushToHistory();
      this.fetchBuildings();
    });
  };

  handleSetCountryFilterSelections = countrySelections => {
    this.setState({ countrySelections }, () => {
      this.pushToHistory();
      this.fetchBuildings();
    });
  };

  clearAllFilters = () => {
    this.setState(
      {
        queryValue: '',
        spaceProviderSelections: [],
        statusSelections: [],
        citySelections: [],
        stateSelections: [],
        countrySelections: [],
      },
      () => {
        this.pushToHistory();
        this.fetchBuildings();
      },
    );
  };

  navigateToExernalProperties = () => {
    const { history } = this.props;

    history.push(routes.admin.vtsPropertySearch);
  };

  changePage = newPage => {
    this.pushToHistory(newPage);
  };

  pushToHistory = (page = 1) => {
    const { history } = this.props;
    const {
      queryValue,
      spaceProviderSelections,
      statusSelections,
      citySelections,
      stateSelections,
      countrySelections,
    } = this.state;

    const params = {
      query: queryValue,
      page,
      space_providers: spaceProviderSelections,
      statuses: statusSelections,
      cities: citySelections,
      states: stateSelections,
      countries: countrySelections,
    };

    const queryString = queryStringSerializer(params);

    history.push(`${routes.admin.buildingSearch}?${queryString}`);
  };

  renderRegionLabel = stateSelections => {
    const { t } = this.props;

    return stateSelections.length > 0
      ? t('filters.stateRegionWithCount', { count: stateSelections.length })
      : t('filters.stateRegion');
  };

  render() {
    const { t } = this.props;
    const {
      loading,
      buildings,
      pagination,
      queryValue,
      cities,
      states,
      countries,
      spaceProviderSelections,
      statusSelections,
      citySelections,
      stateSelections,
      countrySelections,
    } = this.state;

    return (
      <Container
        title={t('building.searchTitle')}
        extra={
          <Button onClick={this.navigateToExernalProperties}>
            {t('building.addBuildingButton')}
          </Button>
        }
      >
        <Form onSubmit={this.onSearchClick}>
          <Form.Item>
            <Row>
              <div className={s.filtersRow}>
                <Input
                  placeholder={t('building.placeholder')}
                  value={queryValue}
                  style={{ width: 225, marginRight: 20, marginTop: 37 }}
                  name="query"
                  prefix={<CustomIcon type="magnifying-glass" className={s.searchIcon} />}
                  onChange={e =>
                    this.setState({ queryValue: e.target.value }, () => {
                      this.onSearch();
                    })
                  }
                />
                <div>
                  <div className={utils.label}>
                    {spaceProviderSelections.length > 0
                      ? t('filters.spaceProviderWithCount', {
                          count: spaceProviderSelections.length,
                        })
                      : t('filters.spaceProvider')}
                  </div>
                  <Select
                    style={{ width: 200, marginRight: 20 }}
                    placeholder={t('building.spaceProvider')}
                    className={s.filterDropdown}
                    onChange={this.handleSetSpaceProviderFilterSelections}
                    mode="multiple"
                    value={spaceProviderSelections}
                    optionFilterProp="children"
                  >
                    {this.renderSpaceProviderFilterOptions()}
                  </Select>
                </div>
                <div>
                  <div className={utils.label}>
                    {statusSelections.length > 0
                      ? t('filters.statusWithCount', { count: statusSelections.length })
                      : t('filters.status')}
                  </div>
                  <Select
                    style={{ width: 150, marginRight: 20 }}
                    placeholder="Status"
                    className={s.filterDropdown}
                    mode="multiple"
                    value={statusSelections}
                    onChange={this.handleSetStatusFilterSelections}
                  >
                    <Select.Option value="inactive">
                      {t('building.statuses.inactive')}
                    </Select.Option>
                    <Select.Option value="onboarding">
                      {t('building.statuses.onboarding')}
                    </Select.Option>
                    <Select.Option value="ready_for_approval">
                      {t('building.statuses.ready_for_approval')}
                    </Select.Option>
                    <Select.Option value="live">{t('building.statuses.live')}</Select.Option>
                    <Select.Option value="retired">{t('building.statuses.retired')}</Select.Option>
                  </Select>
                </div>
                <div>
                  <div className={utils.label}>
                    {citySelections.length > 0
                      ? t('filters.cityWithCount', {
                          count: citySelections.length,
                        })
                      : t('filters.city')}
                  </div>
                  <Select
                    style={{ width: 150, marginRight: 20 }}
                    placeholder="City"
                    className={s.filterDropdown}
                    mode="multiple"
                    value={citySelections}
                    onChange={this.handleSetCityFilterSelections}
                  >
                    {this.renderFilterOptions(cities)}
                  </Select>
                </div>
                <div>
                  <div className={utils.label}>{this.renderRegionLabel(stateSelections)}</div>
                  <Select
                    style={{ width: 150, marginRight: 20 }}
                    placeholder={t('building.stateRegion')}
                    className={s.filterDropdown}
                    mode="multiple"
                    value={stateSelections}
                    onChange={this.handleSetStateFilterSelections}
                  >
                    {this.renderFilterOptions(states)}
                  </Select>
                </div>
                <div>
                  <div className={utils.label}>
                    {countrySelections.length > 0
                      ? t('filters.countryWithCount', { count: countrySelections.length })
                      : t('filters.country')}
                  </div>
                  <Select
                    style={{ width: 150 }}
                    placeholder="Country"
                    className={s.filterDropdown}
                    mode="multiple"
                    value={countrySelections}
                    onChange={this.handleSetCountryFilterSelections}
                    optionFilterProp="label"
                  >
                    {this.renderFilterOptionsForCountries(countries, t)}
                  </Select>
                </div>
                {spaceProviderSelections.length > 0 ||
                statusSelections.length > 0 ||
                citySelections.length > 0 ||
                stateSelections.length > 0 ||
                countrySelections.length > 0 ||
                queryValue ? (
                  <div onClick={this.clearAllFilters} role="button" className={s.clearAllButton}>
                    {t('building.clearAllButton')}
                  </div>
                ) : null}
              </div>
            </Row>
          </Form.Item>
        </Form>
        <BuildingsTable
          buildings={buildings}
          loading={loading}
          actionButtons={this.buildingTableActionButtons}
        />
        <PaginationRow {...pagination} changePage={this.changePage} />
      </Container>
    );
  }
}

RawBuildingSearch.propTypes = {
  history: historyType.isRequired,
  location: locationType.isRequired,
  t: PropTypes.func.isRequired,
  setBreadcrumbs: PropTypes.func.isRequired,
  removeBreadcrumbs: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  setBreadcrumbs: breadcrumbs => dispatch(actions.addBreadcrumbs(breadcrumbs)),
  removeBreadcrumbs: () => dispatch(actions.removeBreadcrumbs()),
});

export default withLDConsumer()(
  flow(withTranslation('admin'), withRouter, connect(null, mapDispatchToProps))(RawBuildingSearch),
);
